import React, { useMemo } from 'react';
import { Card, Button, Progress } from 'antd';
import moment from 'moment';
import { useNavigate } from "react-router-dom";
import SeoScore from './SeoScore';
import CustomIcon from '../../../components/CustomIcon';
import CompetitorSeoScore from './CompetitorSeoScore';
import { CompetitorsScoreData, ScoreData } from '../types';
import useDownloadPdf from '../../../whiteLabel/hooks/useDownloadPdf';
import { useFetchTemplatesQuery } from '../../../whiteLabel/service';
import useStartAnalysis from '../../hooks/useStartAnalysis';


type Props = {
    reportGuid: string;
    url: string;
    scoreData: ScoreData;
    competitorsScoreData?: CompetitorsScoreData;
    totalSectionsCount: number;
    createdAt: string;
    selectedSections: string[];
    analysisProgress?: number;
    isFullReport?: boolean;
    showRefreshReport?: boolean;
    showDownloadPdf?: boolean;
    showWhiteLabel?: boolean;
};

export default function SeoScoreCard({
    reportGuid,
    url,
    scoreData,
    competitorsScoreData,
    totalSectionsCount,
    createdAt,
    analysisProgress,
    isFullReport,
    showRefreshReport,
    showDownloadPdf,
    showWhiteLabel,
    selectedSections
}: Props) {
    const navigate = useNavigate();
    const { startAnalysis } = useStartAnalysis();

    const { downloadPdf, isLoadingPdfReport } = useDownloadPdf();
    const { data: templates } = useFetchTemplatesQuery();

    const competitorScoreEntries = useMemo(() => (competitorsScoreData ? Object.entries(competitorsScoreData) : undefined), [competitorsScoreData]);
    const competitors = competitorScoreEntries ? competitorScoreEntries.map(([url]) => url) : [];
    const isAnalysisInProgress = typeof analysisProgress === 'number' && analysisProgress !== 100;

    const handleRefreshReport = () => {
        startAnalysis(url, competitors, selectedSections);
        navigate("/analysis/report");
    };

    const handlePdfDownload = () => {
        downloadPdf(url, reportGuid, 'default');
    };

    const handleWhiteLabelRedirect = () => {
        if (templates) {
            navigate(`/white-label/reports?template_guid=${templates[0].guid}&report_guid=${reportGuid}&report_url=${encodeURIComponent(url)}`);
        } else {
            navigate(`/white-label/reports?report_guid=${reportGuid}&report_url=${encodeURIComponent(url)}`);
        }
    };


    return (
        <Card
            className="report-card summary"
            id="seo-score"
        >
            <div className="card-header">
                <Card.Meta
                    title={url}
                    description={`Report generated on ${moment(createdAt, 'YYYY-MM-DDTHH:mm:ss').format('MMM DD, YYYY')}`}
                />

                {(showRefreshReport || showDownloadPdf || showWhiteLabel) && (
                    <div className="report-summary-actions seo-report-header">
                        {showRefreshReport && (
                            <Button
                                icon={"sync"}
                                onClick={handleRefreshReport}
                                disabled={isAnalysisInProgress}
                            >
                                Refresh Report
                            </Button>
                        )}

                        {showDownloadPdf && (
                            <Button
                                data-cy="download-report-btn"
                                icon={isLoadingPdfReport ? 'loading' : 'file-pdf'}
                                onClick={handlePdfDownload}
                                disabled={isAnalysisInProgress}
                            >
                                Download PDF
                            </Button>
                        )}

                        {showWhiteLabel && (
                            <Button
                                data-cy={'white-label-btn'}
                                disabled={isAnalysisInProgress}
                                onClick={handleWhiteLabelRedirect}
                            >
                                <CustomIcon
                                    className="icon"
                                    name="brand"
                                    size="18"
                                />
                                White Label
                            </Button>
                        )}
                    </div>
                )}

                {isAnalysisInProgress && (
                    <Progress
                        percent={analysisProgress}
                        status="active"
                        data-cy="analysis-progress"
                    />
                )}
            </div>

            <SeoScore
                scoreData={scoreData}
                totalSectionsCount={totalSectionsCount}
                isAnalysisInProgress={isAnalysisInProgress}
                isFullReport={isFullReport}
            />

            {competitorScoreEntries && competitorScoreEntries.length > 0 && (
                <>
                    <div className="competitors-divider">
                        <span>Competitors</span>
                    </div>
                    <div className="competitor-wrapper">
                        {competitorScoreEntries.map(([url, data]) => (
                            <CompetitorSeoScore
                                key={url}
                                url={url}
                                scoreData={data}
                            />
                        ))}
                    </div>
                </>
            )}
        </Card>
    );
}
